import { type CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop'
import { ScrollingModule } from '@angular/cdk/scrolling'
import * as ngCore from '@angular/core'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeAsserter } from '@attain-apps/type-guards'
import * as ngrxStore from '@ngrx/store'

import * as state from '../../../../../../../../state'

import * as components from './components'

const COLUMN_WIDTH_MULTIPLIER = 160
const COLUMN_MIN_WIDTH = 30 // For column resizing

const ROW_HEIGHT = 41
const DEFAULT_COLUMN_WIDTH = 1

@ngCore.Component({
  encapsulation: ngCore.ViewEncapsulation.None,
  imports: [
    components.Cell.Component,
    ds.illustration.emptyState.Component,
    ds.layout.widget.Component,
    ds.loader.Component,
    DragDropModule,
    ScrollingModule,
  ],
  selector: 'app-report-table',
  styleUrls: ['./report-table.component.scss'],
  templateUrl: './report-table.component.html',
})
export class AtnReportTableComponent {
  private readonly _store = ngCore.inject(ngrxStore.Store)

  private readonly _reportsCollection = ngCore.inject(
    state.rate.reports.Collection,
  )

  public readonly reportPluginSg = ngCore.computed(() =>
    this._reportsCollection?.plugins
      .lookup(state.rate.reports.plugins.content.Plugin)
      .get(),
  )

  private readonly _currentReport =
    this._reportsCollection.plugins.currentEntityPlugin.feature.selectors.getEntity()
      .asSignal

  public readonly loadingSg = ngCore.computed(
    (selectedReport = this._currentReport()) =>
      !selectedReport ||
      this._reportsCollection.plugins.dataOperationPlugin.feature.selectors
        .isLoading(selectedReport.label.id)
        .asSignal(),
  )

  public bodyHeightSg = ngCore.computed(() => {
    let bodySubtractionHeight = 0
    if (this.reportPluginSg()?.feature.selectors.headerRow.asSignal()) {
      bodySubtractionHeight += ROW_HEIGHT
    }
    if (this.reportPluginSg()?.feature.selectors.headerRow.asSignal()) {
      bodySubtractionHeight += ROW_HEIGHT
    }
    return `calc(100% - ${bodySubtractionHeight}px)`
  })
  public columnsWidthSgs: ngCore.WritableSignal<number>[] = []
  public readonly rowsSg = ngCore.computed(
    () =>
      this.reportPluginSg()?.feature.selectors.content.asSignal()?.rows ?? [],
  )

  constructor() {
    ngCore.effect(() => {
      const reportPlugin = this.reportPluginSg()
      if (reportPlugin) {
        // Specify width for each column.
        // If `columns` property is not defined in report content, take total
        // number of columns and add default width for each column.
        // Otherwise, use widths from defined `columns` property.
        let columns = reportPlugin.feature.selectors.columns.asSignal()
        if (!columns?.length) {
          const numberOfColumns =
            reportPlugin.feature.selectors.numberOfColumns.asSignal()
          columns = Array.from({ length: numberOfColumns }, () => ({
            width: DEFAULT_COLUMN_WIDTH,
          }))
        }

        if (columns) {
          this.columnsWidthSgs = columns?.map(column =>
            ngCore.signal(
              column?.width
                ? column.width * COLUMN_WIDTH_MULTIPLIER
                : COLUMN_WIDTH_MULTIPLIER,
            ),
          )
        }
      }
    })
  }

  public dragDropHandler(event: CdkDragDrop<any>, index: number): void {
    const {
      distance: { x: dragWidth },
    } = event
    this.columnsWidthSgs[index].update(prev =>
      prev + dragWidth > COLUMN_MIN_WIDTH ? prev + dragWidth : prev,
    )
  }

  public refreshButtonClickHandler(): void {
    const collection = this._reportsCollection
    const entity = this._currentReport()

    TypeAsserter.assertIsNotNullish(collection)
    TypeAsserter.assertIsNotNullish(entity)

    collection.plugins.dataOperationPlugin.feature.actions.get
      .from('ReportDetails.Table.EmptyState.Buttons.Refresh.Clicked')
      .dispatch({ id: entity.label.id })
  }
}
